// Part of the agent UI for spark#246 / @adr SPARK-0034-ai-agents-are-project-context-not-team-members@2: an AI agent is PROJECT CONTEXT, never a team
// member. Nothing here reaches the solver, and no agent carries a Powers profile — Powers is a
// psychometric instrument built for humans, and putting agents on that scale would argue they are
// interchangeable with candidates, which is the model @adr SPARK-0034-ai-agents-are-project-context-not-team-members@2 rejects.

import { FormattedMessage, useIntl } from "react-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { Modal } from "@/components/design/Modal";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { Disclosure } from "@/components/design/Disclosure";
import { Switch } from "@/components/design/Switch";
import { Tooltip } from "@/components/common/Tooltip";
import { AskAssistantButton } from "@/components/chat/AskAssistantButton";
import { useAgentStore } from "@/stores/agent-store";
import { Permission, useHasPermission } from "@/auth/access-permissions";
import { userErrorMessage } from "@/lib/user-error";
import { AgentRow } from "./AgentRow";
import { AgentCatalogueList } from "./AgentCatalogueList";
import {
  agentNameKey,
  agentPath,
  agentText,
  agentsAdminPath,
  agentsNamedIn,
  displayAgent,
  isAlreadyStored,
  toMatchAgent,
} from "./agents";
import { useAgentsByIds, useFirstAgents } from "./use-agent-search";
import type { AgentListRow } from "./agents";
import type { MatchAgent } from "./design-codec";

/**
 * The seeded hand-off, split in two. The bubble stays a question a person could have typed; the
 * model additionally gets the brief, what is already recorded, and the instance's agent list WITH
 * each agent's coverage, in the reader's language. It has no tool that reads that list, so the
 * hand-off has to carry it.
 *
 * Sending only the names was tried (c597012ae) and made the recommendations noticeably vaguer: the
 * model fell back on whatever it knew about ten product names, and had no project unless it thought
 * to read the match itself. The coverage terms and the brief are what ground the answer.
 *
 * The hidden half says it cannot record the selection: it has no tool for `design.agents`, and an
 * assistant that claims to have added them, or points at a control whose only effect is to ask it
 * again, is the deadlock spark#168/#170 already paid for.
 */
const RECOMMEND_PROMPT_DE = "Welche KI-Agenten passen zu diesem Projekt?";

const RECOMMEND_CONTEXT_DE =
  "Beantworte die Frage anhand der folgenden Angaben. Nenne zwei bis vier Agenten, jeweils mit einem " +
  "Satz Begründung, und sage dazu, welche Anforderungen an die menschlichen Teammitglieder dadurch " +
  "anders aussehen. Agenten sind Projekt-Kontext und nie Teammitglieder. Du kannst die Auswahl nicht " +
  "selbst hinterlegen und hast dafür auch kein Werkzeug: nenne die Agenten namentlich, hinterlegen " +
  "tue ich sie.\n\nProjekt:\n{project}\n\nBereits hinterlegt: {selected}\n\nVerfügbare Agenten:\n{catalogue}";

/**
 * Challenge-step surface: pick the agents this project is equipped with.
 * Replaces the old `<NoBackend>` stub — the copy it carried described a feature that did not exist.
 *
 * SCHALTER UND LISTE IN EINER BOX (spark #1073). Bis hierher sass der Schalter als flache Zeile
 * im Match-Kontext und die Liste, die er einschaltet, drueben in der Anforderungen-Spalte. Das
 * widersprach @adr SPARK-0034-ai-agents-are-project-context-not-team-members@2 nicht im Text,
 * aber in der Platzierung: was neben den Anforderungen steht, liest sich als Anforderung. Die Box
 * ist jetzt der Zwilling der Team-Fit-Box — Chevron und Schalter im Header, Inhalt darunter.
 */
export function AgentPicker({
  agents,
  projectText,
  enabled,
  onEnabledChange,
  onChange,
  readOnly = false,
}: {
  agents: MatchAgent[];
  /** Title + descriptions, joined. Only the assistant hand-off reads it. */
  projectText: string;
  /** `design.agenticAi` — der Schalter im Header dieser Box. */
  enabled: boolean;
  onEnabledChange: (next: boolean) => void;
  onChange: (next: MatchAgent[]) => void;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  // Zugeklappt folgt dem Schalter, danach bedient der Nutzer das Chevron selbst — dieselbe
  // Mechanik wie in TeamContextPanel.
  const [open, setOpen] = useState(enabled);
  // No full list any more (agents scale to about 1000). What the picker knows:
  //  - `firstPage`: the first 100 agents by name (one `agents` page, the most genesis serves),
  //    which the recommend hand-off sends;
  //  - `stored`: the live rows of the agents this match holds, which also says whether a stored
  //    snapshot's id is still a live row (the name dedupe for old or deleted ids hinges on that);
  //  - `seenRows`: every page the choose-agents list has loaded so far.
  // Auto-record matches answers against the union. An agent never loaded in any of them cannot be
  // recorded from an answer; the user adds it from the list.
  const firstPage = useFirstAgents();
  const storedIds = useMemo(() => agents.map((a) => a.id), [agents]);
  const { byId } = useAgentsByIds(storedIds);
  const [seenRows, setSeenRows] = useState<ReadonlyMap<string, AgentListRow>>(new Map());
  const onRowsLoaded = useCallback((rows: readonly AgentListRow[]) => {
    setSeenRows((prev) => (rows.every((r) => prev.get(r.id) === r) ? prev : new Map([...prev, ...rows.map((r) => [r.id, r] as const)])));
  }, []);
  const available = useMemo(() => {
    const all = new Map<string, AgentListRow>();
    for (const agent of [...firstPage.nodes, ...seenRows.values(), ...byId.values()]) all.set(agent.id, agent);
    return [...all.values()];
  }, [firstPage.nodes, seenRows, byId]);
  const agentsReady = firstPage.ready;
  const agentsError = firstPage.error;
  const canEditAgents = useHasPermission(Permission.WriteReferenceData);
  const [browsing, setBrowsing] = useState(false);

  const hasBrief = projectText.trim().length > 0;

  // Agents the assistant names are recorded on their own: it has no tool to write here, so spark
  // applies what it said and the list fills itself while the answer streams.
  //
  // ponytail: a NAME is not an endorsement — "Harvey passt hier nicht" adds Harvey, and the user
  // removes it. Accepted deliberately for the demo, where the list filling itself IS the moment.
  // The durable fix is a machine-readable trailer in the answer, or a real tool in agent-service,
  // rather than reading prose.
  const messages = useAgentStore((s) => s.messages);
  // Whatever is already on screen at MOUNT counts as handled: reopening a thread full of past
  // advice must not replay it into the match. The content is captured once, as it stood when the
  // picker mounted, and on EVERY pass an agent whose name occurs in a message's mount-time content
  // is skipped for that message, judged against the current list. So a row added or renamed later,
  // or a network list that replaces a stale cached one, cannot turn past prose into a pick; only
  // text added after mount records. An answer still streaming at mount goes on recording the
  // agents its later chunks name, and one that arrived during the first load is recorded in full.
  const [mountedContent] = useState(
    () => new Map(useAgentStore.getState().messages.map((m) => [m.id, m.content ?? ""])),
  );
  // (messageId:agentId) pairs already handled. Runs on every render rather than only on `messages`
  // changes — `onChange` is a fresh closure each time — which is harmless: the set makes the pass
  // idempotent, and it keeps the effect free of stale values without reading a ref during render.
  const applied = useRef<Set<string>>(new Set());
  useEffect(() => {
    // No list yet (first load, or a failed one): nothing can be matched, so nothing is marked
    // handled either. The pass runs once a list has actually arrived.
    if (!agentsReady) return;
    const seen = applied.current;
    const namedAtMount = (messageId: string, entry: AgentListRow) => {
      const past = mountedContent.get(messageId);
      return past ? agentsNamedIn(past, [entry]).length > 0 : false;
    };
    const have = new Set(agents.map((a) => a.id));
    const additions: AgentListRow[] = [];
    for (const msg of messages) {
      if (msg.role !== "assistant" || !msg.content) continue;
      for (const entry of agentsNamedIn(msg.content, available)) {
        const key = `${msg.id}:${entry.id}`;
        // Remembered per message, so removing an agent by hand does not invite it straight back
        // when the same answer streams its next chunk.
        if (seen.has(key) || namedAtMount(msg.id, entry)) continue;
        seen.add(key);
        // Two answers naming the same agent in one pass must not add it twice; a snapshot stored
        // under an id that is no live row (old slug, deleted row) counts as present by name (the
        // same rule the list applies).
        if (have.has(entry.id) || isAlreadyStored(entry, agents, byId)) continue;
        have.add(entry.id);
        additions.push(entry);
      }
    }
    // Schalter aus oder nur lesend: gesehen ist gesehen, hinterlegt wird nichts — auch nicht
    // spaeter, wenn das Match wieder beschreibbar wird oder der Schalter angeht. Bis spark #1073
    // hing das an der Montage; die Box steht jetzt immer da, also muss die Sperre hier sitzen,
    // sonst schreibt ein beilaeufig genannter Agentenname Agenten in ein Match, das gar nicht mit
    // ihnen arbeitet.
    if (!enabled || readOnly) return;
    if (additions.length > 0) onChange([...agents, ...additions.map(toMatchAgent)]);
  }, [messages, agents, onChange, readOnly, enabled, available, byId, agentsReady, mountedContent]);

  // Built on click, not on render: the agent list and the brief are both large, and the assistant
  // has no tool that reads the list, so the hand-off has to carry it.
  const recommendContext = () =>
    intl.formatMessage(
      { id: "match.agent.recommendContext", defaultMessage: RECOMMEND_CONTEXT_DE },
      {
        project: projectText.trim(),
        selected: agents.length > 0
          ? agents.map((a) => a.name).join(", ")
          : intl.formatMessage({ id: "match.agent.noneShort", defaultMessage: "keine" }),
        // At most the first 100 agents by name: the hand-off has to fit a prompt, and
        // the model is asked to name agents, not to read an inventory of a thousand.
        catalogue: firstPage.nodes
          .map((e) => `- ${e.name}: ${e.covers.map(agentText).filter(Boolean).join(", ")}`)
          .join("\n"),
      },
    );

  const add = (entry: AgentListRow) => onChange([...agents, toMatchAgent(entry)]);
  const remove = (id: string) => onChange(agents.filter((a) => a.id !== id));
  // Unpicking a genesis row also drops a snapshot whose id is no live row (an old slug, or a row
  // since deleted) that is the same agent only by name; a snapshot of another live row is never
  // touched, whatever it is called.
  const unpick = (entry: AgentListRow) => {
    const key = agentNameKey(entry.name);
    onChange(agents.filter((a) => a.id !== entry.id && (byId.has(a.id) || agentNameKey(a.name) !== key)));
  };

  return (
    <Disclosure
      id="match-agentic-ai-disclosure"
      open={open}
      onToggle={() => setOpen(!open)}
      title={<FormattedMessage id="match.agenticAi" defaultMessage="Agentic AI" />}
      headerAction={
        /* Der erklaerende Satz haengt am Schalter, nicht ueber ihm: er beantwortet die Frage, die
           der Schalter stellt, und die stellt sich nur, wer ihn ansieht. Vorher standen hier zwei
           Saetze dauerhaft im Match-Kontext.

           ponytail: der Satz ist der Verdrahtung VORAUS, bewusst (spark #1073). Er sagt, die
           Anforderungen wuerden passend zu den Agenten abgeleitet — das tut heute nichts:
           `generateAIRequirements` setzt seinen Quelltext aus Brief, Dokumenten und Notizen
           zusammen und sieht `design.agents` nie (apollo-mcp ProposeMatchRequirements.graphql).
           Was es heute schon gibt, ist der Empfehlungs-Handoff unten: der bittet das Modell
           ausdruecklich zu sagen, welche Anforderungen an die Menschen sich dadurch verschieben.
           Einloesen heisst: die Agenten in die Ableitung hineinreichen. */
        <Tooltip placement="left" content={<FormattedMessage id="match.agenticAiBoxDesc" />}>
          <Switch
            id="match-agentic-ai"
            checked={enabled}
            disabled={readOnly}
            onChange={(next) => {
              onEnabledChange(next);
              setOpen(next);
            }}
            ariaLabel={enabled
              ? intl.formatMessage({ id: "match.agenticAiOn", defaultMessage: "Agentic AI aktiv" })
              : intl.formatMessage({ id: "match.agenticAiOff", defaultMessage: "Agentic AI aus" })}
          />
        </Tooltip>
      }
    >
      {/* Ausgeschaltet ist der Inhalt gesperrt, nicht mehr der Weg zu ihm (spark #1084): das
          Chevron klappt jetzt in beiden Schalterzustaenden auf, also muss die Sperre hier unten
          sitzen. Blur + Opacity ist die Blur-Relevanz-Sprache der Zwillingsboxen (Team-Fit,
          Bewertungsstil), inert nimmt Auswahl-Knopf, Entfernen und Assistenten-Handoff aus
          Tab-Reihenfolge UND Interaktion — sonst schriebe man Agenten in ein Match, das gar
          nicht mit ihnen arbeitet. Das Auswahl-Modal steht bewusst AUSSERHALB: Modal rendert
          inline, nicht im Portal, und ein inert gestelltes Modal waere unbedienbar. */}
      <div
        className={`transition ${enabled ? "" : "opacity-60 blur-[2px]"}`}
        inert={!enabled}
        data-testid="agentic-ai-settings"
      >
        {agents.length > 0 ? (
          <div className="space-y-2">
            {agents.map((a) => (
              <AgentRow
                key={a.id}
                {...displayAgent(a, byId.get(a.id))}
                // See SelectedAgentsPanel: no genesis row, no page, so no link.
                to={byId.has(a.id) ? agentPath(a.id) : undefined}
                action={
                  readOnly ? undefined : (
                    <Button
                      variant="icon"
                      size="sm"
                      onClick={() => remove(a.id)}
                      aria-label={intl.formatMessage(
                        { id: "match.agent.remove", defaultMessage: "{name} entfernen" },
                        { name: a.name },
                      )}
                    >
                      <span className="block -translate-y-px text-smycles-title leading-none">×</span> {/* i18n-allow */}
                    </Button>
                  )
                }
              />
            ))}
          </div>
        ) : (
          <div className="text-smycles-meta text-ink-muted">
            <FormattedMessage id="match.agent.none" defaultMessage="Noch keine Agenten hinterlegt." />
          </div>
        )}

        {!readOnly ? (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {/* Same tier as the assistant hand-off beside it, and as "Für mich generieren" further up:
                these are peers doing one job, so the smike glyph is the only thing that may differ. */}
            <Button variant="secondary" size="compact" onClick={() => setBrowsing(true)}>
              <FormattedMessage id="match.agent.browse" defaultMessage="Agenten auswählen" />
            </Button>
            <Tooltip
              placement="top"
              content={
                hasBrief
                  ? intl.formatMessage({
                      id: "match.agent.recommendHint",
                      defaultMessage: "Öffnet den Assistenten, der aus deiner Beschreibung passende Agenten vorschlägt.",
                    })
                  : intl.formatMessage({ id: "match.challenge.aiFillFirstHint", defaultMessage: "Erst eine Beschreibung ausfüllen." })
              }
            >
              <AskAssistantButton
                prompt={intl.formatMessage({ id: "match.agent.recommendPrompt", defaultMessage: RECOMMEND_PROMPT_DE })}
                hiddenContext={recommendContext}
                // Not before the list is there: the hand-off would tell the model there are no agents.
                disabled={!hasBrief || !agentsReady}
                label={<FormattedMessage id="match.agent.recommend" defaultMessage="Agenten empfehlen" />}
              />
            </Tooltip>
          </div>
        ) : null}
      </div>

      <Modal
        open={browsing}
        onClose={() => setBrowsing(false)}
        title={<FormattedMessage id="match.agent.catalogueTitle" defaultMessage="Verfügbare Agenten" />}
        // Wider than the 32rem default and than the detail sheet below: AgentRow clamps the role to
        // two lines, and at 42rem that cut most of the longer entries mid-sentence. Width buys
        // characters per line, not more lines, so the longest roles still clamp; the fix for those
        // is shorter prose, not a wider modal.
        maxWidth="56rem"
      >
        {agentsError ? (
          <p role="alert" className="text-smycles-meta text-ink">{userErrorMessage(intl, agentsError)}</p>
        ) : agentsReady && firstPage.nodes.length === 0 ? (
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-smycles-meta text-ink-muted">
            <FormattedMessage id="match.agent.catalogueEmpty" defaultMessage="No AI agents have been created yet." />
            {canEditAgents ? (
              <Link to={agentsAdminPath()} className={buttonClasses({ variant: "ghost", size: "sm" })}>
                <FormattedMessage id="match.agent.catalogueEmptyAdmin" defaultMessage="Create them in master data" />
              </Link>
            ) : null}
          </div>
        ) : null}
        {/* Rendered while the first page is still loading too: the list shows its own loading state. */}
        {!agentsError && !(agentsReady && firstPage.nodes.length === 0) ? (
          <AgentCatalogueList
            onRowsLoaded={onRowsLoaded}
            isSelected={(entry) => isAlreadyStored(entry, agents, byId)}
            onToggle={(entry) => (isAlreadyStored(entry, agents, byId) ? unpick(entry) : add(entry))}
          />
        ) : null}
      </Modal>
    </Disclosure>
  );
}
